import { motion } from "framer-motion";
import LogoAnimatedSvg from "./LogoAnimatedSvg";
import { heroStats, brand } from "../data/site";

const heroLines = [
    { text: "Building", accent: false },
    { text: "Landmarks.", accent: true },
    { text: "Creating Legacies.", accent: false },
];

export default function Hero() {
    return (
        <section
            id="top"
            className="relative min-h-[100vh] w-full bg-[#0b0b0b] overflow-hidden"
            data-testid="hero-section"
        >
            {/* Background image with heavy vignette */}
            <div className="absolute inset-0">
                <motion.img
                    initial={{ scale: 1.15 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 2.4, ease: [0.2, 0.7, 0.2, 1] }}
                    src="images/illustration-construction-site.jpg"
                    alt="Luxury Bangalore construction"
                    className="w-full h-full object-cover opacity-70"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-[#0b0b0b]/70 via-[#0b0b0b]/50 to-[#0b0b0b]" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#0b0b0b]/85 via-transparent to-[#0b0b0b]/60" />
            </div>

            {/* Blueprint grid overlay */}
            <svg
                className="absolute inset-0 w-full h-full opacity-[0.06]"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden
            >
                <defs>
                    <pattern id="grid" width="60" height="60" patternUnits="userSpaceOnUse">
                        <path
                            d="M 60 0 L 0 0 0 60"
                            fill="none"
                            stroke="#C89B3C"
                            strokeWidth="0.5"
                        />
                    </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#grid)" />
            </svg>

            {/* Top meta bar */}
            <div className="relative z-10 pt-[100px] md:pt-[120px] px-6 md:px-10">
                <div className="mx-auto max-w-[1440px] flex items-center justify-between">
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.9, duration: 0.8 }}
                        className="text-[10px] uppercase tracking-[0.4em] text-[#bebebe]"
                    >
                        <span className="text-[#C89B3C]">◆</span>&nbsp;&nbsp;Est. 1984
                        &nbsp;·&nbsp; Bangalore
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.1, duration: 0.8 }}
                        className="hidden md:block text-[10px] uppercase tracking-[0.4em] text-[#bebebe]"
                    >
                        Chapter 01 — The Legacy
                    </motion.div>
                </div>
            </div>

            {/* Main content grid */}
            <div className="relative z-10 px-6 md:px-10 mt-[8vh] md:mt-[10vh]">
                <div className="mx-auto max-w-[1440px] grid grid-cols-12 gap-6">
                    {/* Vertical label */}
                    <div className="hidden md:flex col-span-1 items-start justify-center pt-4">
                        <span className="vwrite text-[10px] uppercase tracking-[0.4em] text-[#C89B3C]">
                            Since MCMLXXXIV — Bangalore
                        </span>
                    </div>

                    <div className="col-span-12 md:col-span-11 lg:col-span-8">
                        {/* Kinetic headline with masked line-by-line reveal */}
                        <h1
                            className="font-display font-light leading-[0.92] tracking-tight text-white text-[13vw] md:text-[8.5vw] lg:text-[7.6vw]"
                            data-testid="hero-headline"
                        >
                            {heroLines.map((line, i) => (
                                <span
                                    key={i}
                                    className="block overflow-hidden pb-[0.14em]"
                                    aria-label={line.text}
                                >
                                    <motion.span
                                        initial={{ y: "110%" }}
                                        animate={{ y: "0%" }}
                                        transition={{
                                            duration: 1.05,
                                            delay: 0.3 + i * 0.18,
                                            ease: [0.2, 0.7, 0.2, 1],
                                        }}
                                        className={`block ${line.accent ? "italic text-[#E2C56D]" : ""}`}
                                    >
                                        {line.text}
                                    </motion.span>
                                </span>
                            ))}
                        </h1>

                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 1, delay: 1.1 }}
                            className="mt-8 md:mt-10 max-w-xl text-[#bebebe] text-base md:text-lg leading-relaxed font-light"
                            data-testid="hero-sub"
                        >
                            Premium Builders, Developers &amp; Promoters in Bangalore since
                            1984. Delivering thoughtfully designed residential, commercial and
                            villa projects with uncompromising quality.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 1.3 }}
                            className="mt-10 md:mt-12 flex flex-wrap items-center gap-4"
                        >
                            <a
                                href="#projects"
                                className="btn-sheen inline-flex items-center gap-3 px-7 py-4 bg-[#C89B3C] text-[#0b0b0b] text-[11px] uppercase tracking-[0.32em] font-medium hover:bg-[#E2C56D] transition-colors"
                                data-testid="hero-explore-btn"
                            >
                                <span className="relative z-[2]">Explore Projects</span>
                                <span className="relative z-[2]">→</span>
                            </a>
                            <a
                                href="#contact"
                                className="inline-flex items-center gap-3 px-7 py-4 border border-[rgba(212,175,55,0.55)] text-white/95 text-[11px] uppercase tracking-[0.32em] font-medium hover:bg-white/5 hover:border-[#E2C56D] transition-colors"
                                data-testid="hero-consult-btn"
                            >
                                Schedule a Consultation
                            </a>
                        </motion.div>
                    </div>

                    {/* Right column: floating logo mark & watermark */}
                    <div className="col-span-12 lg:col-span-3 relative min-h-[220px]">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 1.4, delay: 0.4 }}
                            className="lg:absolute lg:right-0 lg:top-6"
                        >
                            <div className="relative">
                                <LogoAnimatedSvg size={240} />
                            </div>
                        </motion.div>
                    </div>
                </div>
            </div>

            {/* Bottom stat block */}
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 1.5 }}
                className="relative z-10 mt-[9vh] md:mt-[11vh] px-6 md:px-10 pb-10"
            >
                <div className="mx-auto max-w-[1440px] grid grid-cols-2 md:grid-cols-4 border-t border-[rgba(212,175,55,0.25)]">
                    {heroStats.map((s, i) => (
                        <div
                            key={s.label}
                            className={`py-6 md:py-8 pr-6 ${
                                i !== heroStats.length - 1
                                    ? "md:border-r md:border-[rgba(212,175,55,0.18)]"
                                    : ""
                            }`}
                            data-testid={`hero-stat-${i}`}
                        >
                            <div className="font-display text-4xl md:text-5xl text-[#E2C56D] tracking-tight">
                                {s.value}
                            </div>
                            <div className="mt-2 text-[10px] uppercase tracking-[0.3em] text-[#bebebe]">
                                {s.label}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="mx-auto max-w-[1440px] mt-6 flex items-center justify-between text-[10px] uppercase tracking-[0.35em] text-[#7a7a7a]">
                    <span className="flex items-center gap-3">
                        <span className="w-8 h-px bg-[#C89B3C]" /> Scroll to continue
                    </span>
                    <span className="hidden md:inline">{brand.fullName}</span>
                </div>
            </motion.div>
        </section>
    );
}
